/**
 * MH-35 Ma trận quyền theo nhóm (đợt 3 phần C4, yêu cầu nghiệp vụ mục 1).
 *
 * Bảng kiểm (checkbox) theo nhóm quyền nghiệp vụ; bốn quyền "nguy hiểm" hiển thị
 * nổi bật ở khối riêng đầu trang. Đổi quyền LUÔN qua hai bước: xem trước (số
 * người dùng bị ảnh hưởng, quyền thêm/bớt) rồi mới xác nhận ghi — máy chủ tự thi
 * hành lại đúng quy tắc này (PATCH thiếu `xacNhan` chỉ trả xem trước).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import {
  quanTriApi,
  LoiApi,
  type DongNhom,
  type DongQuyen,
  type KetQuaDoiQuyenNhom,
} from '../api/quan-tri-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';

export function MH35NhomQuyen(): ReactNode {
  const [moc, datMoc] = useState(0);
  const [nhomChonId, datNhomChonId] = useState<string | null>(null);
  const [quyenDangChon, datQuyenDangChon] = useState<Set<string>>(new Set());
  const [xemTruoc, datXemTruoc] = useState<KetQuaDoiQuyenNhom | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [loiGui, datLoiGui] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);

  const kq = dungTaiDuLieu<{ nhom: DongNhom[]; quyen: DongQuyen[] }>({
    tai: (signal) => quanTriApi.danhSachNhom(signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [moc],
  });

  const taiLai = (): void => datMoc((x) => x + 1);

  const nhomChon = kq.duLieu?.nhom.find((n) => n.id === nhomChonId) ?? null;
  const quyenTheoNhomNghiepVu = useMemo(() => {
    const nhom = new Map<string, DongQuyen[]>();
    for (const q of kq.duLieu?.quyen ?? []) {
      if (!nhom.has(q.nhom)) nhom.set(q.nhom, []);
      nhom.get(q.nhom)!.push(q);
    }
    return nhom;
  }, [kq.duLieu]);
  const quyenNguyHiemDayDu = (kq.duLieu?.quyen ?? []).filter((q) => q.nguyHiem);

  const chonNhom = (n: DongNhom): void => {
    datNhomChonId(n.id);
    datQuyenDangChon(new Set(n.quyen));
    datXemTruoc(null);
    datLoiGui(null);
  };

  const doiCheckbox = (ma: string): void => {
    datXemTruoc(null);
    datQuyenDangChon((truoc) => {
      const moi = new Set(truoc);
      if (moi.has(ma)) moi.delete(ma);
      else moi.add(ma);
      return moi;
    });
  };

  const guiXemTruoc = async (): Promise<void> => {
    if (!nhomChonId) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      const r = await quanTriApi.doiQuyenNhom(nhomChonId, { quyen: [...quyenDangChon], xacNhan: false });
      datXemTruoc(r);
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không xem trước được');
    } finally {
      datDangGui(false);
    }
  };

  const guiApDung = async (): Promise<void> => {
    if (!nhomChonId || !xemTruoc) return;
    if (!window.confirm(vi.nhomQuyen.xacNhanApDung)) return;
    datDangGui(true);
    datLoiGui(null);
    try {
      await quanTriApi.doiQuyenNhom(nhomChonId, { quyen: [...quyenDangChon], xacNhan: true });
      datThongBao(vi.nhomQuyen.daApDung);
      datXemTruoc(null);
      taiLai();
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không áp dụng được');
    } finally {
      datDangGui(false);
    }
  };

  if (kq.trangThai === 'dang-tai') return <KhungXuong soCot={4} />;
  if (kq.trangThai === 'loi' && kq.loi) return <TrangThaiLoi loi={kq.loi} onThuLai={taiLai} />;

  return (
    <div className="man-hinh man-hinh--nhom-quyen">
      <header className="man-hinh__dau">
        <h1>{vi.nhomQuyen.tieuDe}</h1>
      </header>
      {thongBao ? <p role="status">{thongBao}</p> : null}

      <div className="man-hinh__bo-cuc-hai-cot">
        <ul data-vung="danh-sach-nhom" role="list">
          {(kq.duLieu?.nhom ?? []).map((n) => (
            <li key={n.id}>
              <button
                type="button"
                className={`nut${nhomChonId === n.id ? ' nut--chon' : ''}`}
                onClick={() => chonNhom(n)}
              >
                {n.ten} ({dienVaoSo(vi.nhomQuyen.soNguoiDung, n.soNguoiDung)})
              </button>
            </li>
          ))}
        </ul>

        {nhomChon ? (
          <div data-vung="ma-tran-quyen">
            <section className="the-canh-bao the-canh-bao--nguy-hiem" data-vung="quyen-nguy-hiem">
              <h2>{vi.nhomQuyen.quyenNguyHiem}</h2>
              <p>{vi.nhomQuyen.ghiChuNguyHiem}</p>
              <ul role="list">
                {quyenNguyHiemDayDu.map((q) => (
                  <li key={q.ma}>
                    <label>
                      <input
                        type="checkbox"
                        checked={quyenDangChon.has(q.ma)}
                        onChange={() => doiCheckbox(q.ma)}
                      />
                      {q.moTa} <code>{q.ma}</code>
                    </label>
                  </li>
                ))}
              </ul>
            </section>

            {[...quyenTheoNhomNghiepVu.entries()].map(([nhomNghiepVu, ds]) => (
              <section key={nhomNghiepVu}>
                <h3>{nhomNghiepVu}</h3>
                <ul role="list">
                  {ds
                    .filter((q) => !q.nguyHiem)
                    .map((q) => (
                      <li key={q.ma}>
                        <label>
                          <input
                            type="checkbox"
                            checked={quyenDangChon.has(q.ma)}
                            onChange={() => doiCheckbox(q.ma)}
                          />
                          {q.moTa} <code>{q.ma}</code>
                        </label>
                      </li>
                    ))}
                </ul>
              </section>
            ))}

            {loiGui ? <p role="alert">{loiGui}</p> : null}

            {!xemTruoc ? (
              <button type="button" className="nut nut--chinh" onClick={() => void guiXemTruoc()} disabled={dangGui}>
                {vi.nhomQuyen.xemTruoc}
              </button>
            ) : (
              <div className="hop-thoai" data-vung="xem-truoc-doi-quyen">
                <p>
                  {vi.nhomQuyen.soNguoiBiAnhHuong}: <strong>{xemTruoc.soNguoiDungBiAnhHuong}</strong>
                </p>
                {xemTruoc.quyenThem.length > 0 ? (
                  <p>
                    {vi.nhomQuyen.quyenThem}: {xemTruoc.quyenThem.join(', ')}
                  </p>
                ) : null}
                {xemTruoc.quyenBot.length > 0 ? (
                  <p>
                    {vi.nhomQuyen.quyenBot}: {xemTruoc.quyenBot.join(', ')}
                  </p>
                ) : null}
                {xemTruoc.coQuyenNguyHiem ? (
                  <p className="the the--canh-bao">{vi.nhomQuyen.ghiChuNguyHiem}</p>
                ) : null}
                <button type="button" className="nut nut--chinh" onClick={() => void guiApDung()} disabled={dangGui}>
                  {vi.nhomQuyen.apDung}
                </button>
                <button type="button" className="nut" onClick={() => datXemTruoc(null)}>
                  {vi.nhomQuyen.huy}
                </button>
              </div>
            )}
          </div>
        ) : (kq.duLieu?.nhom.length ?? 0) === 0 ? (
          <p>{vi.nhomQuyen.rongMoTa}</p>
        ) : (
          <p>{vi.nhomQuyen.chonNhomDeXem}</p>
        )}
      </div>
    </div>
  );
}

function dienVaoSo(mau: string, so: number): string {
  return `${so} ${mau.toLowerCase()}`;
}
